<template>
  <div v-if="largeEnough">
    <div v-if="impulse" class="impulse-nesting__values">
      <button-item @click="openOverlay(affectedItem)">
        <span v-if="affectedItemTitle">{{ affectedItemTitle }}</span>
        <em v-else>{{ $t("labels.unnamed") }}</em>
        <template #after-text>
          <span
            v-if="beforeVisible && impulse?.valueAfter != null && impulse?.valueBefore != null"
            class="impulse-nesting__change"
          >
            {{ $n(impulse.valueBefore, "decimal") }}
          </span>
        </template>
      </button-item>

      <span class="impulse-nesting__amount">
        <span v-if="impulseSetValue != null">={{ impulseSetValue }}</span>
        <strong
          v-if="impulseAddValue != null"
          :class="{
            'impulse-nesting__not-fully-applied': notFullyApplied
          }"
          :title="
            notFullyApplied
              ? `${formatNumber(addedValue, 'points')} / ${formatNumber(
                  impulseAddValue,
                  'points'
                )}`
              : undefined
          "
        >
          <span> {{ formatNumber(percentValue, "points") }} </span
          ><span class="impulse-nesting__unit">
            {{ affectedItem?.unit || "" }}
          </span>
        </strong>
      </span>
    </div>

    <div
      v-if="childrenOpen && childImpulses.length > 0"
      class="impulse-nesting__children list mb-0"
    >
      <div
        class="impulse-nesting__opener"
        @click="childrenOpen = !childrenOpen"
      ></div>

      <nesting-impulse
        v-for="childImpulse in childImpulses"
        :key="'chimp' + roundIndex + childImpulse.srcId"
        :source-type="childImpulse.srcType"
        :source-id="childImpulse.srcId"
        :simulation-id="simulationId"
        :round-index="childImpulse.roundIndex"
        :affected-type="childImpulse.affectedType"
        :affected-id="childImpulse.affectedId"
        :impulses="impulses"
        :multiplicator="totalElementValueChange ? percentValue / totalElementValueChange : 0"
      />
    </div>

    <div
      v-if="!childrenOpen && childImpulses.length > 0"
      class="impulse-nesting__children-placeholder"
      @click="childrenOpen = !childrenOpen"
    >
      <span>
        +{{ childImpulses.length }}
        {{ $t("words.indirectEffects", childImpulses.length) }}.</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { get, find, clone, cloneDeep } from "lodash-es";

import { formatNumber } from "@/helpers/utility/Formatting";
import { showError } from "@core/ui/alert/alert";

import ButtonItem from "@core/input/ButtonItem.vue";

import { useModelStore } from "@editor/items/model/model.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";

import type { ImpulseItem } from "./ImpulseItem";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";

const props = withDefaults(defineProps<{
  sourceType: string;
  sourceId: number | string;
  simulationId: number;
  roundIndex: number;
  affectedType: "target" | "resource";
  affectedId: number;
  impulses: {
    [roundIndex: number]: {
      target: { [targetId: number]: ImpulseItem };
      resource: { [resourceId: number]: ImpulseItem };
    };
  };
  multiplicator?: number;
  forceVisible?: boolean;
  beforeVisible?: boolean;
}>(), {
  multiplicator: 1,
  forceVisible: false,
  beforeVisible: true
});

const { t } = useI18n();

const childrenOpen = ref(false);

const impulse = computed(() => {
  return get(props.impulses, [
    props.roundIndex,
    props.affectedType,
    props.affectedId
  ]);
});

const totalElementValueChange = computed(() => {
  if (!impulse.value) return 0;

  // calculate the amount the impulse really increased by:
  const beforeValue = impulse.value.valueBefore || 0;
  const addedPoints = impulse.value.pointsToAdd || 0;
  let effectiveDelta = 0;
  if (impulse.value.valueAfter != null) {
    effectiveDelta =
      impulse.value.valueAfter - beforeValue;
  } else {
    const setValue = impulse.value.valueToSet ?? beforeValue;
    effectiveDelta = (setValue + addedPoints) - beforeValue;
  }

  return effectiveDelta;
});

const sourceValue = computed(() => {
  if (!impulse.value) return null;

  let valueSource = find(
    impulse.value.sourceValues,
    (value) => value.type === props.sourceType && value.id === props.sourceId
  );
  if (!valueSource) {
    console.log(
      "ERROR finding valueSource",
      clone(impulse.value),
      props.sourceType,
      props.sourceId,
      cloneDeep(impulse.value.sourceValues)
    );
    return null;
  }
  return valueSource;
});

const impulseAddValue = computed(() => {
  return sourceValue.value?.value;
});

const impulseSetValue = computed(() => {
  console.log("sourceValue", sourceValue.value);
  return sourceValue.value?.valueToSet;
});

const percentAdded = computed(() => {
  if (!impulse.value) return 1;
  if (impulse.value.pointsToAdd == null) return 1;
  if (impulse.value.pointsToAdd == 0) return 1;

  return totalElementValueChange.value / impulse.value.pointsToAdd;
});

const addedValue = computed(() => {
  return (impulseAddValue.value || 0) * percentAdded.value;
});

const percentValue = computed(() => {
  return props.multiplicator * addedValue.value;
});

const largeEnough = computed(() => {
  if (props.forceVisible) return true;
  if (!impulse.value) return false;
  return Math.abs(percentValue.value) > 0.01;
});

const notFullyApplied = computed(() => {
  return (
    percentAdded.value < 1
  );
});

const connectedRelations = computed(() => {
  const elementUid = `${props.affectedType}_${props.affectedId}`;

  return useRelationStore().activeByStartUid[elementUid] || [];
});

const childImpulses = computed(() => {
  // don't show any child impulses while the impulse is not applied
  if (impulse.value.valueAfter == null) return [];

  // stop following the impulse if it's value is under the threshold
  if (Math.abs(percentValue.value) < 0.01) return [];

  return connectedRelations.value.map((relation) => {
    return {
      srcType: "relation",
      srcId: relation.id,
      affectedType: relation.endItemType,
      affectedId: relation.endItemId,
      roundIndex: props.roundIndex + 1 + relation.delay
    };
  });
});


const affectedItem = computed(() => {
  return useModelStore().getItem(props.affectedType, props.affectedId);
});

const affectedItemTitle = computed(() => {
  if (!affectedItem.value) return "";
  if (!("title" in affectedItem.value)) return "";
  return affectedItem.value?.title;
});


function openOverlay(item: OverlayDialogItemRequest | null) {
  if (item) {
    useOverlay().setItem(item);
  } else {
    showError(t("errors.itemDoesNotExist"));
  }
}
</script>

<style lang="scss">
.impulse-nesting {
  display: block;

  &__children {
    position: relative;
    padding-left: 1rem;
    padding-block-start: 0.5rem;
    gap: 0.75rem;
  }
  &__children-placeholder {
    padding-block-start: 0.25rem;
    padding-left: 0.5em;
    &:hover {
      color: var(--color-action);
    }
  }

  &__opener {
    display: block;
    position: absolute;
    top: -0.125em;
    left: 0.25em;
    width: 0.5em;
    bottom: 0;
    background-color: var(--color-background-medium);
    &:hover {
      background-color: var(--color-action);
    }
  }

  &__values {
    display: grid;
    grid-template-columns: 1fr minmax(0, 4rem);
    gap: 0.5rem;
    align-items: center;
  }
  &__change {
    white-space: nowrap;
    margin-left: 0.5em;
  }
  &__amount {
    width: 4rem;
    overflow: hidden;
    text-align: right;
    white-space: nowrap;
    font-weight: 600;
  }
  &__not-fully-applied {
    color: grey;
    text-decoration: dotted underline var(--color-grey-dark);
    text-underline-offset: 2px;
  }
  &__unit {
    display: none;
    font-weight: 400;
    font-size: 12px;
    color: var(--color-text-light);
    padding-left: 2px;
  }
}
</style>
